import { EffectComposer }  from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass }      from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { ShaderPass }      from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { OutputPass }      from 'three/examples/jsm/postprocessing/OutputPass.js'
import { FXAAShader }      from 'three/examples/jsm/shaders/FXAAShader.js'
import * as THREE          from 'three'

const VignetteShader = {
  uniforms: {
    tDiffuse:  { value: null },
    offset:    { value: 0.98 },
    darkness:  { value: 0.18 },   // light theme: barely-there edge shading
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform float offset;
    uniform float darkness;
    varying vec2 vUv;
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      vec2 uv = (vUv - 0.5) * vec2(1.0, 1.4);
      float vignette = 1.0 - dot(uv, uv);
      vignette = clamp(pow(vignette, darkness) / offset, 0.0, 1.0);
      color.rgb *= vignette;
      gl_FragColor = color;
    }
  `
}

// Chromatic aberration — pure RGB channel separation toward screen edges.
// No blur, no artifacts. Subtle always-on base + peaks during face closeup (P2).
const LensBlurShader = {
  uniforms: {
    tDiffuse: { value: null },
    strength: { value: 0.25 },  // 0.25 base, peaks at 1.0 during closeup
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform float strength;
    varying vec2 vUv;
    void main() {
      vec2 center = vec2(0.5, 0.5);
      vec2 toEdge = vUv - center;
      float dist  = length(toEdge);
      vec2 dir    = toEdge / max(dist, 0.0001);

      // CA amount: quadratic falloff so centre stays clean, edges separate
      float ca = dist * dist * strength * 0.007;

      float r = texture2D(tDiffuse, vUv + dir * ca).r;
      float g = texture2D(tDiffuse, vUv           ).g;
      float b = texture2D(tDiffuse, vUv - dir * ca).b;

      gl_FragColor = vec4(r, g, b, 1.0);
    }
  `
}

// Animated film grain — adds texture, reduces banding on dark backgrounds
const GrainShader = {
  uniforms: {
    tDiffuse:  { value: null },
    time:      { value: 0.0 },
    strength:  { value: 0.022 },   // light theme: grain reads stronger on white
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform float strength;
    varying vec2 vUv;

    float rand(vec2 co) {
      return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
    }

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      float grain = rand(vUv + fract(time * 0.07)) * strength - strength * 0.5;
      color.rgb += grain;
      gl_FragColor = color;
    }
  `
}

export function buildPostFX(renderer, scene, camera, isMobile = false) {
  // Half-float render target — fixes colour banding in dark gradients
  // Must use drawing buffer size (accounts for DPR) to avoid pixelation
  const drawSize = renderer.getDrawingBufferSize(new THREE.Vector2())
  const rt = new THREE.WebGLRenderTarget(drawSize.width, drawSize.height, {
    // HalfFloat on both platforms: fixes colour banding in dark gradients and
    // gives VFX additive blending proper HDR headroom before tone-mapping clamps it.
    // MSAA omitted — MSAA+HalfFloat resolve has browser/GPU compat issues.
    type: THREE.HalfFloatType,
  })
  const composer = new EffectComposer(renderer, rt)

  // 1. Normal render
  const renderPass = new RenderPass(scene, camera)
  composer.addPass(renderPass)

  // 2. Bloom — both platforms (essential for VFX particle glow)
  //    Mobile: quarter-res for performance, slightly lower strength
  const bloomScale = isMobile ? 0.25 : 0.5
  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(Math.floor(window.innerWidth * bloomScale), Math.floor(window.innerHeight * bloomScale)),
    isMobile ? 0.15 : 0.18,   // strength
    0.40,                       // radius
    2.5                         // threshold — must exceed the HDR sky base (~2.0) or the whole bg blooms into a white veil
  )
  composer.addPass(bloomPass)

  // 3. Chromatic aberration — disabled on mobile (saves a full-screen pass)
  const lensBlurPass = new ShaderPass(LensBlurShader)
  lensBlurPass.enabled = !isMobile
  composer.addPass(lensBlurPass)

  // 4. Output (tone-mapping / colour-space correction)
  const outputPass = new OutputPass()
  composer.addPass(outputPass)

  // 5. Vignette
  const vignettePass = new ShaderPass(VignetteShader)
  composer.addPass(vignettePass)

  // 6. Film grain — breaks up 8-bit colour banding in dark sky gradients
  const grainPass = new ShaderPass(GrainShader)
  grainPass.enabled = !isMobile
  composer.addPass(grainPass)

  // 7. FXAA — smooths jagged model edges lost when rendering through EffectComposer
  // Disabled on mobile (saves a full-screen pass; DPR 1.0 benefit is minimal)
  const fxaaPass = new ShaderPass(FXAAShader)
  fxaaPass.enabled = !isMobile
  fxaaPass.material.uniforms['resolution'].value.set(
    1 / (window.innerWidth  * renderer.getPixelRatio()),
    1 / (window.innerHeight * renderer.getPixelRatio())
  )
  composer.addPass(fxaaPass)

  return { composer, bloomPass, lensBlurPass, grainPass }
}
